import InvoiceCellRenderer from '../../utility/agGrid/InvoiceCellRenderer';
import ResendInviteRenderer from '../../utility/agGrid/ResendInviteRenderer';
import ResetSubPwordRenderer from 'application/ui/utility/agGrid/ResetSubPwordRenderer';
import DeleteContactLogRenderer from '../../utility/agGrid/DeleteContactLogRenderer';
import {ReactComponent as LoadingIcon} from '../../media/icon-loading.svg';
import {ReactComponent as AddIcon} from '../../media/icon-add.svg';
import {ReactComponent as CloseIcon} from '../../media/icon-close.svg';
import {addRewardsAPI} from '../../../api';
import {useStore} from '../../../store';
import useBreadcrumbs from 'application/ui/utility/routing/useBreadcrumbs';
import DataGrid, { GridButton } from 'application/ui/utility/agGrid/DataGrid';
import {
  getAccountDetailsAPI,
  getSubAccountsAPI,
  getResidentListAPI,
  getInvoiceListAPI,
  getContactLogAPI,
  updateContactLogAPI
} from '../../../api';
import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  Dispatch,
  SetStateAction
} from 'react';
import {useParams} from 'react-router-dom';
import {TextField, Checkbox, FormControlLabel} from '@mui/material';
import FacilityFields from '../auth/authentication/register/entities/FacilityFields'
import {
	allCountries,
	CountryData,
	Region
} from 'country-region-data';
import SelectCounty from 'application/ui/utility/location/SelectCounty';
import SelectRegion from 'application/ui/utility/location/SelectRegion';
import SelectCountry from 'application/ui/utility/location/SelectCountry';
import AccountFields from '../auth/authentication/register/entities/AccountFields';
import Shipping from 'application/store/auth/entities/Shipping';
import Company from 'application/store/auth/entities/Company';
import EditedAccount from 'application/api/auth/entities/EditedAccount';
import handleReCAPTCHA from 'application/ui/utility/google/handleReCAPTCHA';
import { useRecaptchaContext } from 'application/ui/context/recaptcha';
import { FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { authService } from 'application/utility';
import CustomTextAreaEditor from 'application/ui/utility/agGrid/CustomTextAreaEditor';
import {dateFormatter} from 'application/ui/utility/agGrid/DateFormatter';

type FacSubAccount = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  dateCreated: string;
};

type FacResident = {
  firstName: string;
  lastName: string;
  sex: 'M' | 'F',
  clothingSize: string;
  shoeSize: string;
  budget: string;
};

type FacInvoice = {
  id: string;
  date: Date;
  resident: string;
  total: string;
  invoiceId: number;
};

type FacContactLog = {
  logID?: number;
  userId: string;
  date: string;
  type: string;
  contact: string;
  message: string;
}

//Facility Grids Section
type FacDetailGridProps = {
  type: 'sub' | 'res' | 'inv' | 'contactLogs';
  rowData: Array<FacSubAccount> | Array<FacResident> | Array<FacInvoice> | Array<FacContactLog>;
  onAddContactLog?: () => void;
  onDeleteContactLog?: (logData: any) => void;
  onCellValueChanged?: (params: any) => void;
};

/**
 * Grid component for the Facility Detail Page.
 * Creates 3 types of grids depending on the given props.
 * @param type Determines the appropriate data structure for the grid.
 * sub = subaccounts, res = residents, inv = invoices, contactLogs = contacts with facility.
 * @param rowData
 * @returns Grid component
 */
const FacDetailGrid = ({type, rowData, onAddContactLog, onDeleteContactLog, onCellValueChanged}: FacDetailGridProps) => {
  const contactLogTypes = ['Email', 'Phone', 'In-Person', 'Package', 'DELETE'];
  const defaultColDef = {
    resizable: true,
    sortable: true,
    minWidth: 100,
  };
  const columnDefsSub = [
    {field: 'dateCreated', headerName: 'Date Created', filter: 'agDateColumnFilter', minWidth: 50, maxWidth: 150},
    {field: 'title', headerName: 'Title', filter: 'agTextColumnFilter'},
    {field: 'firstName', headerName: 'First Name', filter: 'agTextColumnFilter'},
    {field: 'lastName', headerName: 'Last Name', filter: 'agTextColumnFilter'},
    {field: 'email', headerName: 'Email', filter: 'agTextColumnFilter'},
    {field: 'invite', headerName: 'Resend Invite', cellRenderer: ResendInviteRenderer},
    {field: 'reset password', headerName: 'Reset Password', cellRenderer: ResetSubPwordRenderer}
  ];
  const columnDefsRes = [
    {field: 'firstName', headerName: 'First Name', filter: 'agTextColumnFilter'},
    {field: 'lastName', headerName: 'Last Name', filter: 'agTextColumnFilter'},
    {field: 'sex', headerName: 'Sex', filter: 'agTextColumnFilter', minWidth: 50, maxWidth: 100},
    {field: 'clothingSize', headerName: 'Clothing Size', filter: 'agTextColumnFilter'},
    {field: 'shoeSize', headerName: 'Shoe Size', filter: 'agTextColumnFilter'},
    {field: 'budget', headerName: 'Budget', filter: 'agNumberColumnFilter'}
  ];
  const columnDefsInv = [
    {field: 'date', headerName: 'Date', filter: 'agDateColumnFilter', minWidth: 50, maxWidth: 125},
    {field: 'invoiceId', headerName: 'Invoice ID', filter: 'agNumberColumnFilter', cellRenderer: InvoiceCellRenderer,  minWidth: 50, maxWidth: 125},
    {field: 'resident', headerName: 'Resident', filter: 'agTextColumnFilter', minWidth: 100, maxWidth: 300},
    {field: 'total', headerName: 'Total', filter: 'agNumberColumnFilter'}
  ];
  const columnDefsContactLogs = [
    {
      field: 'date',
      headerName: 'Date',
      filter: 'agDateColumnFilter',
      minWidth: 150,
      maxWidth: 200,
      editable: true
    },
    {
      field: 'type',
      // cellRenderer: SportRenderer,
      cellEditor: 'agSelectCellEditor',
      editable: true,
      cellEditorParams: {
        values: contactLogTypes,
        // cellRenderer: SportRenderer,
      },
      valueFormatter: (params: any) => `${params.value}`,
      valueParser: (params: any) => String(params.newValue),
      minWidth: 100,
      maxWidth: 200
    },
    {field: 'contact', editable: true, headerName: 'Contact', filter: 'agTextColumnFilter', minWidth: 100, maxWidth: 250},
    {
      field: 'message',
      editable: true,
      headerName: 'Notes',
      filter: 'agTextColumnFilter',
      cellStyle: { whiteSpace: 'pre-line' },
      autoHeight: true,
      cellEditor: CustomTextAreaEditor, // Use the custom editor
      valueFormatter: (params: any) => {
        // Convert \n strings to actual line breaks for display
        return params.value ? params.value.replace(/\n/g, '<br/>') : '';
      },
      valueParser: (params: any) => {
        // Return the new value as-is (line breaks will be converted by the backend)
        return params.newValue;
      }
    },
    {
      field: 'delete',
      headerName: 'Delete',
      cellRenderer: DeleteContactLogRenderer,
      cellRendererParams: {
        onDelete: onDeleteContactLog
      },
      minWidth: 80,
      maxWidth: 80,
      sortable: false,
      filter: false,
      editable: false
    }
  ];

  let curColDefs;
  let buttons: GridButton[] = [];
  
  switch (type) {
    case 'sub':
      curColDefs = columnDefsSub;
      break;
      
    case 'res':
      curColDefs = columnDefsRes;
      break;

    case 'contactLogs':
      curColDefs = columnDefsContactLogs;
      if (onAddContactLog) {
        buttons = [
          {
            displayText: 'Add',
            styling: 'secondary',
            onClick: onAddContactLog
          }
        ];
      }
      break;

    default:
      curColDefs = columnDefsInv;
      break;
  }

  return <DataGrid
    rowData={rowData}
    defaultColDef={defaultColDef}
    columnDefs={curColDefs}
    paginationPageSize={5}
    domLayout='autoHeight'
    buttons={buttons}
    onCellValueChanged={onCellValueChanged}
  />;
};


//Rewards Popup Section
type FacRewards = {
  rewards: string;
  rewardsExpiration: string;
};

type RewardsPopupProps = {
  userId: string;
  setToggleRewardPopup: Dispatch<SetStateAction<boolean>>;
  setRewards: Dispatch<SetStateAction<FacRewards>>;
};

/**
 * Popup component that allows an admin to change a facility's reward amount/expiration.
 * @param userId
 * @param setToggleRewardPopup Action that triggers this component
 * @param setRewards Action that updates rewards amount/expiration
 * @returns Rewards popup component
 */
const RewardsPopup = ({userId, setToggleRewardPopup, setRewards}: RewardsPopupProps) => {
    const admin = useStore((state) => state.admin);
    const dispatchSetAlert = useStore((state) => state.dispatchSetAlert);
    const [addRewardsNote, setAddRewardsNote] = useState('');
    const [addRewardsAmount, setAddRewardsAmount] = useState('');
    const [isSendingRewardsEmail, toggleIsSendingRewardsEmail] = useState(false);
    
    const setFacFieldsRewards = (e: ChangeEvent<HTMLInputElement>) =>
    setAddRewardsAmount(e.target.value);
    
    const submitAddRewards = async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
    
      if (!admin) return dispatchSetAlert({
        status: 'danger',
        header: 'Invalid Credentials',
        text: 'Invalid admin credentials.'
      });
    
      const res = await addRewardsAPI({
        adminID: admin.id,
        userID: userId,
        reward_amount: Number(addRewardsAmount),
        reward_note: addRewardsNote,
        sendEmail: isSendingRewardsEmail ? 1 : undefined
      });
      
      console.log(res);//TESTING

      setRewards({
        rewards: res.data.newRewardAmount.toString(),
        rewardsExpiration: res.data.newRewardExpirationDate
      });
      
      dispatchSetAlert({
        status: 'success',
        header: 'Success',
        text: 'Rewards have been added.'
      });
      
      setAddRewardsAmount('');
      setAddRewardsNote('');
      setToggleRewardPopup(false);
    };
    

  return (
    <div className='rewards_popup rewards_popup--add'>
      <div className='rewards_popup__main'>
        <div className='rewards_popup__banner'>
          <button
            className='rewards_popup__close'
            onClick={() => setToggleRewardPopup(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <form
          className='rewards_popup__prompt'
          onSubmit={submitAddRewards}
        >
          <h3>How much would you like to add?</h3>
          <TextField
            label='Rewards ($)'
            type='number'
            value={addRewardsAmount}
            onChange={setFacFieldsRewards}
            required />
          <TextField
            label='Notes'
            value={addRewardsNote}
            onChange={(e) => setAddRewardsNote(e.target.value)} />
          <FormControlLabel
            className='rewards_popup__checkbox'
            label='Send Email'
            control={
              <Checkbox
                name='substitutions'
                checked={isSendingRewardsEmail}
                onChange={() => toggleIsSendingRewardsEmail(!isSendingRewardsEmail)}
              />
            }
          />
          <button
            className='btn btn--primary'
            type='submit'
          >
            Add
          </button>
        </form>
      </div>
    </div>
  );
};


//Facility Detail Page Section
/**
 * Custom hook to manage state and actions for facility information.
 * @param userId 
 * @param setRewards Action that updates reward amount/expiration.
 * This is required on this component because the rewards data originates from the same api as the facility information.
 * @returns Facility info, and loading state to indicate that data is being fetched
 */
const useFacilityInfo = (userId: string, setRewards: Dispatch<SetStateAction<FacRewards>>) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<CountryData | undefined>();
	const [selectedRegion, setSelectedRegion] = useState<Region | undefined>();
	const [selectedCounty, setSelectedCounty] = useState<string | undefined>();
  const recaptchaRef = useRecaptchaContext();
	const dispatchEditAccount = useStore((state) => state.dispatchEditAccount);
	const dispatchSetAlert = useStore((state) => state.dispatchSetAlert);

	const [accountFields, setAccountFields] = useState({
		username: '',
		email: '',
		plainPassword: '',
		plainPasswordConfirm: '',
		firstName: '',
		lastName: '',
		title: '',
		phoneNumber: '',
    roleId: '',
    userId: ''
	});
	const [facFields, setFacFields] = useState({
		companyName: '',
		companyPhoneNumber: '',
		fax: '',
		address1: '',
		address2: '',
		city: '',
		state: '',
		zip: '',
		country: '',
		admin: '',
		bom: '',
		bomEmail: '',
		wantsNewsletter: false,
    taxrate: ''
	});

  const getFacilityInfo = async () => {
    const res = await getAccountDetailsAPI(userId);
    setFacFields({
      companyName: res.data.facName,
      companyPhoneNumber: res.data.facPhone,
      fax: res.data.facFax,
      address1: res.data.facAddress1,
      address2: res.data.facAddress2,
      city: res.data.facCity,
      state: res.data.facState,
      zip: res.data.facZip,
      country: res.data.facCountry,
      admin: res.data.facAdmin,
      bom: res.data.facOfficeMgr,
      bomEmail: res.data.facOfficeMgrEmail,
      wantsNewsletter: res.data.newsletter === '1' ? true : false,
      taxrate: res.data.taxrate
    });

    setAccountFields({
      username: res.data.username,
      email: res.data.email,
      plainPassword: '',
      plainPasswordConfirm: '',
      firstName: res.data.firstName,
      lastName: res.data.lastName,
      title: res.data.title,
      phoneNumber: res.data.phoneNumber,
      userId: res.data.userID,
      roleId: res.data.roleID
    })

    const matchingCountry = allCountries.find(
      (country) => country[1] === res.data.facCountry
    );

    if (matchingCountry) {
      setSelectedCountry(matchingCountry);

      const matchingRegion = matchingCountry[2].find(
        (region) => region[1] === res.data.facState
      );

      if (matchingRegion) setSelectedRegion(matchingRegion);
			if (res.data.facCounty) setSelectedCounty(res.data.facCounty);
		}

    // add id and tax edit here

    setRewards({
      rewards: res.data.facRewards,
      rewardsExpiration: res.data.facRewardsExpiration
    });

    setIsLoading(false);
  };

  const updatePartialFacFields = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFacFields({
      ...facFields,
      [event.target.name]: event.target.value 
    })
  };

  const updatePartialAccFields = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAccountFields({
      ...accountFields,
      [event.target.name]: event.target.value 
    })
  };

	const onSubmit =
	async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

    
		if (
			!selectedCountry ||
			!selectedRegion ||
      (accountFields.plainPassword && accountFields.plainPassword !== accountFields.plainPasswordConfirm)
		) return dispatchSetAlert({
      status: 'danger',
      header: 'Missing Fields',
      text: 'Please make sure all required fields are completed and passwords match if included.'
    });

		let shipping: Shipping;
		let company: Company;
		let editedAccount: EditedAccount;
		
    shipping = {
      address1: facFields.address1,
      address2: facFields.address2,
      city: facFields.city,
      county: selectedCounty,
      state: selectedRegion[1],
      country: selectedCountry[1],
      zip: facFields.zip
    };

    company = {
      name: facFields.companyName,
      phoneNumber: facFields.companyPhoneNumber,
      admin: facFields.admin,
      officeManager: {
        name: facFields.bom,
        email: facFields.bomEmail
      },
      fax: facFields.fax
    };

    editedAccount = {
      userID: accountFields.userId,
      roleID: Number(accountFields.roleId),
      username: accountFields.username,
      email: accountFields.email,
      firstName: accountFields.firstName,
      lastName: accountFields.lastName,
      password: accountFields.plainPassword && accountFields.plainPasswordConfirm ? 
      {
        hashedOldPassword: '',
        hashedNewPassword: authService.password.hash(accountFields.plainPassword),
        hashedNewPasswordConfirm: authService.password.hash(accountFields.plainPasswordConfirm)
      }: 
      undefined,
      title: accountFields?.title || '',
      phoneNumber: accountFields.phoneNumber,
      company: company,
      shipping: shipping
    };

    // if resetting password, add those fields...

		const recaptchaToken = await handleReCAPTCHA(recaptchaRef);

		dispatchEditAccount(
			editedAccount,
			false,
			recaptchaToken
		)
	};

  useEffect(() => {getFacilityInfo()}, []);
  
  return {
    accountFields,
    setAccountFields,
    facFields,
    setFacFields,
    selectedCounty,
    setSelectedCounty,
    selectedRegion,
    setSelectedRegion,
    selectedCountry,
    setSelectedCountry,
    updatePartialAccFields,
    updatePartialFacFields,
    isLoading,
    onSubmit
  };
};
  
/**
 * Custom hook to manage state and actions for facility subaccounts.
 * @param userId 
 * @returns Facility subaccounts, and loading state to indicate that data is being fetched
 */
const useSubAccounts = (userId: string) => {
  const [subAccounts, setSubAccounts] = useState<Array<FacSubAccount>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getSubAccounts = async () => {
    const res = await getSubAccountsAPI(userId);
    setSubAccounts(res.data.map((sub) => ({
      id: sub.ID,
      firstName: sub.fName,
      lastName: sub.lName,
      email: sub.email,
      title: sub.title,
      dateCreated: sub.createdDate
    })));

    setIsLoading(false);
  };

  useEffect(() => {getSubAccounts()}, []);

  return {subAccounts, isLoading};
};

/**
 * Custom hook to manage state and actions for facility residents.
 * @param userId
 * @returns facility residents, and loading state to indicate that data is being fetched
 */
const useResidents = (userId: string) => {
  const [residents, setResidents] = useState<Array<FacResident>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getResidents = async () => {
    const res = await getResidentListAPI(Number(userId));
    setResidents(res.data.map((resident) => ({
      firstName: resident.fName,
      lastName: resident.lName,
      sex: resident.sex,
      clothingSize: resident.clothing_size,
      shoeSize: resident.shoe_size,
      budget: resident.budget_amount
    })));

    setIsLoading(false);
  };

  useEffect(() => {getResidents()}, []);
  
  return {residents, isLoading};
};

/**
 * Custom hook to manage state and actions for facility invoices.
 * @param userId 
 * @returns Facility invoices, and loading state to indicate that data is being fetched
 */
const useInvoices = (userId: string) => {
  const [invoices, setInvoices] = useState<Array<FacInvoice>>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const getInvoices = async () => {
    const res4 = await getInvoiceListAPI(Number(userId));
    setInvoices(res4.data.map((inv) => ({
      id: userId,
      date: inv.date,
      resident: inv.residentFullName,
      total: inv.total_cost,
      invoiceId: inv.invoice_ID
    })));

    setIsLoading(false);
  };

  useEffect(() => {getInvoices()}, []);

  return {invoices, isLoading};
};

/**
 * Custom hook to manage state and actions for facility contact logs.
 * @param userId 
 * @returns Facility contact logs, and loading state to indicate that data is being fetched
 */
const useContactLogs = (userId: string) => {
  const [contactLogs, setContactLogs] = useState<Array<FacContactLog>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const dispatchSetAlert = useStore((state) => state.dispatchSetAlert);
  
  const getContactLogs = async (userId: string) => {
    try {
      const res = await getContactLogAPI(userId);
      if (!res) throw new Error('No response from server');

      const formattedLogs = res.map((log) => ({
        logID: log.logID,
        userId: log.userID.toString(),
        date: log.date,
        type: log.type,
        contact: log.contact,
        message: log.message
      }));

      setContactLogs(formattedLogs);

    } catch (error) {
      dispatchSetAlert({
        status: 'danger',
        header: 'Error Fetching Contact Logs',
        text: 'There was an error fetching the contact logs. Please try again later.'
      });
    }

    setIsLoading(false);
  };

  const updateContactLog = async (logData: FacContactLog) => {
    try {
      const requestData = {
        logID: logData.logID,
        type: logData.type,
        contact: logData.contact,
        date: logData.date,
        userID: logData.userId,
        message: logData.message
      };

      await updateContactLogAPI(requestData);

      // Refresh contact logs after updating
      await getContactLogs(userId);

      dispatchSetAlert({
        status: 'success',
        header: 'Success',
        text: 'Contact log has been updated.'
      });

    } catch (error) {
      dispatchSetAlert({
        status: 'danger',
        header: 'Error Updating Contact Log',
        text: 'There was an error updating the contact log. Please try again later.'
      });
    }
  };

  const deleteContactLog = async (logData: FacContactLog) => {
    try {
      if (!logData.logID) {
        dispatchSetAlert({
          status: 'danger',
          header: 'Cannot Delete',
          text: 'Cannot delete a contact log entry that has not been saved.'
        });
        return;
      }

      const requestData = {
        logID: logData.logID,
        type: 'DELETE',
        contact: logData.contact,
        date: logData.date,
        userID: logData.userId,
        message: logData.message
      };

      await updateContactLogAPI(requestData);

      // Refresh contact logs after deletion
      await getContactLogs(userId);

      dispatchSetAlert({
        status: 'success',
        header: 'Success',
        text: 'Contact log has been deleted.'
      });

    } catch (error) {
      dispatchSetAlert({
        status: 'danger',
        header: 'Error Deleting Contact Log',
        text: 'There was an error deleting the contact log. Please try again later.'
      });
    }
  };

  const onCellValueChanged = async (params: any) => {
    const updatedRowData = params.data;
    await updateContactLog(updatedRowData);
  };

  const onAddContactLog = async () => {
    try {
      // Create a new contact log entry with default values
      const newContactLog: FacContactLog = {
        userId: userId,
        date: new Date().toISOString().split('T')[0],
        type: 'Email',
        contact: '',
        message: ''
      };

      const requestData = {
        type: newContactLog.type,
        contact: newContactLog.contact,
        date: newContactLog.date,
        userID: newContactLog.userId,
        message: newContactLog.message
      };

      await updateContactLogAPI(requestData);

      // Refresh contact logs after adding
      await getContactLogs(userId);

      dispatchSetAlert({
        status: 'success',
        header: 'Success',
        text: 'New contact log has been added.'
      });

    } catch (error) {
      dispatchSetAlert({
        status: 'danger',
        header: 'Error Adding Contact Log',
        text: 'There was an error adding the contact log. Please try again later.'
      });
    }
  };

  useEffect(() => {getContactLogs(userId)}, []);

  return {contactLogs, onAddContactLog, onDeleteContactLog: deleteContactLog, onCellValueChanged, isLoading};
};


type Params = {
  userId: string;
};

/**
 * Page that details a single facility
 * @returns Page component
*/
const FacilityDetail = () => {
  const {userId} = useParams<Params>();
  useBreadcrumbs([
    {
      id: 0,
      name: 'Dashboard',
      path: '/admin/dashboard'
    },
    {
      id: 2,
      name: 'Facilities',
      path: '/admin/lookup/facility'
    }
  ]);
  const [toggleRewardPopup, setToggleRewardPopup] = useState(false);
  const [rewards, setRewards] = useState({
    rewards: '',
    rewardsExpiration: ''
  });
  const {
    accountFields,
    setAccountFields,
    facFields,
    selectedCounty,
    setSelectedCounty,
    selectedRegion,
    setSelectedRegion,
    selectedCountry,
    setSelectedCountry,
    updatePartialAccFields,
    updatePartialFacFields,
    onSubmit,
    isLoading: isInfoLoading
  } = useFacilityInfo(userId, setRewards);
  const {subAccounts, isLoading: isSubLoading} = useSubAccounts(userId);
  const {residents, isLoading: isResLoading} = useResidents(userId);
  const {invoices, isLoading: isInvLoading} = useInvoices(userId);
  const {contactLogs, onAddContactLog, onDeleteContactLog, onCellValueChanged, isLoading: isContactLogsLoading} = useContactLogs(userId);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(
    () => {
      if (
        !isInfoLoading &&
        !isSubLoading &&
        !isResLoading &&
        !isInvLoading &&
        !isContactLogsLoading
      ) setIsLoading(false);
    }, [isInfoLoading, isSubLoading, isResLoading, isInvLoading, isContactLogsLoading]
  );
    
  if (isLoading) return (
    <div className='a-fac-detail'>
      <LoadingIcon />
    </div>
  ); 

  return (
  <>
      <div className='a-fac-detail'>
        <form onSubmit={onSubmit}>
          <h3>Primary Account Info</h3>
          <TextField
            label='Username'
            name='username'
            variant='standard'
            value={accountFields.username}
            onChange={updatePartialAccFields}
            required
          />
          <TextField
            type='email'
            label='Email'
            name='email'
            variant='standard'
            value={accountFields.email}
            onChange={updatePartialAccFields}
            required
          />
          <TextField
            label='First Name'
            name='firstName'
            variant='standard'
            value={accountFields.firstName}
            onChange={updatePartialAccFields}
            required
          />
          <TextField
            label='Last Name'
            name='lastName'
            variant='standard'
            value={accountFields.lastName}
            onChange={updatePartialAccFields}
            required
          />
          <TextField
            type='password'
            label='New Password'
            name='plainPassword'
            variant='standard'
            value={accountFields.plainPassword}
            onChange={updatePartialAccFields}
            inputProps={{minLength: 8}}
            required={accountFields.plainPasswordConfirm.length > 0}
            disabled
          />
          <TextField
            type='password'
            label='New Password Confirm'
            name='plainPasswordConfirm'
            variant='standard'
            value={accountFields.plainPasswordConfirm}
            onChange={updatePartialAccFields}
            inputProps={{minLength: 8}}
            required={accountFields.plainPassword.length > 0}
            disabled
          />
          <FormControl>
            <InputLabel id='edit-account-title'>Title</InputLabel>
            <Select
              labelId='edit-account-title'
              label='title'
              value={accountFields.title}
              onChange={(e) => setAccountFields({...accountFields, title: typeof e.target.value === 'string' ? e.target.value : '' })}
              required
            >
              <MenuItem value='Administration'>Administration</MenuItem>
              <MenuItem value='Business Office Staff'>Business Office Staff</MenuItem>
              <MenuItem value='Social Services'>Social Services</MenuItem>
              <MenuItem value='Activities/Recreation'>Activities/Recreation</MenuItem>
              <MenuItem value='Central Supply/Housekeeping'>Central Supply/Housekeeping</MenuItem>
              <MenuItem value='Laundry'>Laundry</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label='Phone Number'
            name='phoneNumber'
            variant='standard'
            value={accountFields.phoneNumber}
            onChange={updatePartialAccFields}
            required
          />       
          <h3>Facility Info</h3>
          <TextField
            label='Facility Name'
            name='companyName'
            variant='standard'
            value={facFields.companyName}
            onChange={updatePartialFacFields}
            required
          />
          <TextField
            label='Admin'
            name='admin'
            variant='standard'
            value={facFields.admin}
            onChange={updatePartialFacFields}
            required
          />
          <TextField
            label='Business Office Manager'
            name='bom'
            variant='standard'
            value={facFields.bom}
            onChange={updatePartialFacFields}
            required
          />
          <TextField
            label={`Business Office Manager's Email`}
            name='bomEmail'
            variant='standard'
            value={facFields.bomEmail}
            onChange={updatePartialFacFields}
            required
          />
          <TextField
            label='Facility Phone Number'
            name='companyPhoneNumber'
            variant='standard'
            value={facFields.companyPhoneNumber}
            inputProps={{minLength: 7}}
            onChange={updatePartialFacFields}
            required
          />
          <TextField
            label='Fax'
            name='fax'
            variant='standard'
            value={facFields.fax}
            onChange={updatePartialFacFields}
          />
          <TextField
            label='Address 1'
            name='address1'
            variant='standard'
            value={facFields.address1}
            onChange={updatePartialFacFields}
          />
          <TextField
            label='Address 2'
            name='address2'
            variant='standard'
            value={facFields.address2}
            onChange={updatePartialFacFields}
          />
          <TextField
            label='City'
            name='city'
            variant='standard'
            value={facFields.city}
            onChange={updatePartialFacFields}
          />
          <SelectCounty
            selectedCountry={selectedCountry}
            selectedRegion={selectedRegion}
            selectedCounty={selectedCounty}
            setSelectedCounty={setSelectedCounty}
            variant='standard'
            required={false}
          />
          <SelectRegion
            selectedCountry={selectedCountry}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
            variant='standard'
          />
          <TextField
            type='number'
            label='Zip Code'
            name='zip'
            variant='standard'
            value={facFields.zip}
            onChange={updatePartialFacFields}
          />
          <SelectCountry
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            variant='standard'
          />
          <TextField
            label='Taxrate'
            name='taxrate'
            variant='standard'
            value={facFields.taxrate}
            disabled
          />
          <input
            className='btn btn--secondary'
            type='submit'
            value='Save Facility Info'
          />
        </form>
        <div className='a-fac-detail__rewards'>
          <h5>Rewards:</h5>
          <span>
            <p>{rewards.rewards}</p>
            <button onClick={() => setToggleRewardPopup(true)}>
              <AddIcon />
            </button>
          </span>
          <li>
            <h5>Rewards Exp.</h5>
            <p>{rewards.rewardsExpiration}</p>
          </li>
        </div>
        <div>
          <h3>Contact Log</h3>
          <FacDetailGrid
            type='contactLogs'
            rowData={contactLogs}
            onAddContactLog={onAddContactLog}
            onDeleteContactLog={onDeleteContactLog}
            onCellValueChanged={onCellValueChanged}
          />
        </div>
        <div>
          <h3>Authorized Users</h3>
          <FacDetailGrid
            type='sub'
            rowData={subAccounts}
          />
        </div>
        <div>
          <h3>Resident List</h3>
          <FacDetailGrid
            type='res'
            rowData={residents}
          />
        </div>
        <div>
          <h3>Order History</h3>
          <FacDetailGrid
            type='inv'
            rowData={invoices}
          />
        </div>
      </div>
      {
        toggleRewardPopup &&
        <RewardsPopup
          userId={userId}
          setRewards={setRewards}
          setToggleRewardPopup={setToggleRewardPopup}
        />
      }
    </>
  );
};

export default FacilityDetail;